import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { INITIAL_STATE } from "../customer-form/types";
import { sanitize, stripTags, validate } from "../customer-form/utils";
import {
  RATE_LIMIT_MS,
  RATE_LIMIT_KEY,
  countryEmailMap,
} from "../customer-form/constants";
import templateData from "../customer-form/template.json";
import { safeLocalStorageGet } from "../utils/safeJsonParse";

import GeneralFields from "../customer-form/GeneralFields";
import CountrySpecificFields from "../customer-form/CountrySpecificFields";
import SubjectField from "../customer-form/SubjectField";
import DescriptionField from "../customer-form/DescriptionField";
import TicketDetailsFields from "../customer-form/TicketDetailsFields";
import CustomerFileAttachment from "../customer-form/CustomerFileAttachment";
import StatusToast from "../customer-form/StatusToast";
import { submitTicket, uploadNocobaseAttachments } from "../services/api";
import { toast } from "react-toastify";
import T, { useTranslate } from "../components/T";

import "../styles/customer-form.css";

const SendIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16 }}
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

export default function CustomerFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [activeSection, setActiveSection] = useState("customer");
  const rightRef = useRef(null);
  const { tText } = useTranslate();

  const queryParams = new URLSearchParams(location.search);
  const isOnBehalf = queryParams.get("onBehalf") === "true";
  const isKenya = formData.country === "Kenya";

  const steps = [
    { id: "customer", label: "Customer info", desc: "Their contact details" },
    {
      id: "ticket",
      label: "Ticket details",
      desc: "Type, reason & description",
    },
    ...(isKenya
      ? [
          {
            id: "kenya",
            label: "Kenya specifics",
            desc: "Country required data",
          },
        ]
      : []),
    { id: "attachments", label: "Attachments", desc: "Relevant files & logs" },
  ];

  // IntersectionObserver — highlight sidebar step as user scrolls
  useEffect(() => {
    const container = rightRef.current;
    if (!container) return;
    const sections = container.querySelectorAll("[data-section]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting)
            setActiveSection(entry.target.dataset.section);
        });
      },
      { root: container, rootMargin: "-20% 0px -60% 0px", threshold: 0 },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [isKenya]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const setAllTouched = () => {
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
  };

  const getFieldErrors = () => {
    const errs = {};
    if (!formData.country) errs.country = "This field is required";
    if (!formData.contactName) errs.contactName = "This field is required";
    if (!formData.email) errs.email = "This field is required";
    if (!formData.phone) errs.phone = "This field is required";
    if (!formData.subject) errs.subject = "This field is required";
    if (!formData.description) {
      errs.description = "This field is required";
    } else if (formData.description.trim().length <= 10) {
      errs.description = "Description must be more than 10 characters";
    }
    return errs;
  };
  
  const errors = getFieldErrors();

  const handleCaseTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      caseType: value,
      caseReason: "",
      subReason: "",
    }));
  };

  const handleCaseReasonChange = (value) => {
    setFormData((prev) => ({ ...prev, caseReason: value, subReason: "" }));
  };

  const handleAddFiles = async (newFiles) => {
    // 1. Initialise the newly dropped files as "uploading"
    const newAttachments = newFiles.map(file => ({
      file,
      id: null,
      url: null,
      isUploading: true,
      error: null
    }));

    setFormData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments],
    }));

    // 2. Perform the upload in the background
    try {
      const results = await uploadNocobaseAttachments(newFiles);
      
      setFormData((prev) => {
        // Map over current attachments, if it's one of the new files, update it
        const nextAttachments = prev.attachments.map(att => {
          // Identify the newly uploaded files by comparing the exact File object
          const index = newFiles.indexOf(att.file);
          if (index !== -1 && results[index]) {
            return {
              ...att,
              id: results[index].id,
              url: results[index].url,
              isUploading: false
            };
          }
          return att;
        });
        return { ...prev, attachments: nextAttachments };
      });
    } catch (err) {
      setFormData((prev) => {
        const nextAttachments = prev.attachments.map(att => {
          if (newFiles.includes(att.file)) {
            return { ...att, isUploading: false, error: err.message };
          }
          return att;
        });
        return { ...prev, attachments: nextAttachments };
      });
    }
  };

  const removeAttachment = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const buildSubmissionPayload = (data, attachmentUrls = []) => {
    const variables = {
      subject: data.subject || `Support Request from ${data.contactName}`,
      country: data.country,
      caseType: data.caseType,
      caseReason: data.caseReason,
      subReason: data.subReason || "N/A",
      issueType: data.issueType || "N/A",
      location: data.location || "N/A",
      make: data.make || "N/A",
      model: data.model || "N/A",
      carReg: data.carReg || "N/A",
      documentType: data.documentType || "N/A",
      phone: data.phone,
      description: data.description,
      name: data.contactName,
      email: data.email,
    };
    return {
      name: templateData.id || templateData.name,
      variables,
      channel: "EMAIL",
      recipients: [countryEmailMap[data.country] || "hodi@autochek.com"],
      subject: variables.subject,
      cc: [data.email],
      metadata: {
        origin: isOnBehalf ? "staff-on-behalf" : "cx-support-form",
        originId: data.email,
        correlationId: `req-${Date.now()}`,
        submittedBy: isOnBehalf
          ? safeLocalStorageGet("staff_profile").primaryEmail || "staff"
          : "customer",
      },
      attachments: attachmentUrls,
    };
  };

  const formValid = !!(
    formData.country &&
    formData.contactName &&
    formData.email &&
    formData.phone
  );

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setStatus({ type: "idle", message: "" });
    if (formData.hpWebsite) return;

    const lastSubmit = parseInt(
      sessionStorage.getItem(RATE_LIMIT_KEY) || "0",
      10,
    );
    if (Date.now() - lastSubmit < RATE_LIMIT_MS) {
      const wait = Math.ceil(
        (RATE_LIMIT_MS - (Date.now() - lastSubmit)) / 1000,
      );
      setStatus({
        type: "error",
        message: `Please wait ${wait}s before resubmitting.`,
      });
      return;
    }

    const error = validate(formData);
    if (error) {
      setStatus({ type: "error", message: error });
      return;
    }

    setStatus({ type: "loading", message: "Submitting request..." });

    try {
      if (formData.attachments?.some((a) => a.isUploading)) {
        setStatus({
          type: "error",
          message: "Please wait for all files to finish uploading before submitting.",
        });
        return;
      }

      const attachmentUrls = formData.attachments.map(a => a.url).filter(url => url);

      const cleanData = {
        ...formData,
        contactName: stripTags(sanitize(formData.contactName)),
        subject: stripTags(sanitize(formData.subject)),
        description: stripTags(sanitize(formData.description)),
      };
      const payload = buildSubmissionPayload(cleanData, attachmentUrls);
      await submitTicket(payload);
      sessionStorage.setItem(RATE_LIMIT_KEY, Date.now().toString());
      setStatus({
        type: "success",
        message: "✅ Ticket submitted successfully!",
      });
      toast.success(tText("Support ticket submitted successfully!"));
      setFormData(INITIAL_STATE);
      setTimeout(() => navigate(isOnBehalf ? "/staff/dashboard" : "/"), 2000);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("Submission error:", err);
      }
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setStatus({
        type: "error",
        message: `Submission failed: ${errorMessage}`,
      });
      if (
        errorMessage.toLowerCase().includes("auth failed") ||
        errorMessage.toLowerCase().includes("token")
      ) {
        toast.error(tText("An error has occurred, please try again later."));
        setFormData(INITIAL_STATE);
        setTimeout(() => navigate(isOnBehalf ? "/staff/dashboard" : "/"), 3000);
      } else {
        toast.error(`Error: ${errorMessage}`);
      }
    }
  };

  return (
    <div className="cx-page">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-orb sidebar-orb-1" />
        <div className="sidebar-content">
          <div className="sidebar-badge">
            <span className="sidebar-badge-dot" />
            <span>{isOnBehalf ? <T>Staff Submission</T> : <T>Customer Support</T>}</span>
          </div>

          <h1 className="sidebar-title">
            {isOnBehalf ? <T>Customer</T> : <T>Submit a</T>} <br />
            <span className="sidebar-accent">
              {isOnBehalf ? <T>Experience</T> : <T>Support Ticket</T>}
            </span>
          </h1>

          <p className="sidebar-sub">
            {isOnBehalf
              ? <T>Submit a report or complaint on behalf of an Autochek customer.</T>
              : <T>Provide the details of your request and our team will get back to you within ~2h.</T>}
          </p>

          <nav className="steps">
            {steps.map((step) => {
              const isActive = activeSection === step.id;
              const activeIndex = steps.findIndex(
                (s) => s.id === activeSection,
              );
              const stepIndex = steps.findIndex((s) => s.id === step.id);
              const isCompleted = stepIndex < activeIndex;
              return (
                <div
                  key={step.id}
                  className={`step-item ${isActive ? "active" : ""} ${
                    isCompleted ? "completed" : ""
                  }`}
                  onClick={() => {
                    const el = document.getElementById(`section-${step.id}`);
                    if (el && rightRef.current)
                      rightRef.current.scrollTo({
                        top: el.offsetTop - 80,
                        behavior: "smooth",
                      });
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className="step-number">
                    {isCompleted ? "✓" : stepIndex + 1}
                  </div>
                  <div className="step-content">
                    <span className="step-label"><T>{step.label}</T></span>
                    <span className="step-desc"><T>{step.desc}</T></span>
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="sidebar-footer">
            <p><T>© {new Date().getFullYear()} Autochek Support</T></p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content" ref={rightRef}>
        <div className="form-container">
          <nav className="breadcrumb">
            <Link
              to={isOnBehalf ? "/staff/dashboard" : "/"}
              className="breadcrumbLink"
            >
              {isOnBehalf ? <T>Help Center</T> : <T>Home</T>}
            </Link>
            <span className="breadcrumbSep">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ width: 14 }}
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </span>
            <span className="breadcrumbCurrent">
              {isOnBehalf ? <T>Customer Experience</T> : <T>Customer Support</T>}
            </span>
          </nav>

          <form
            onSubmit={(e) => e.preventDefault()}
            className="cx-continuous-form"
          >
            {/* Honeypot */}
            <input
              type="text"
              name="hpWebsite"
              style={{ display: "none" }}
              value={formData.hpWebsite}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />

            {/* Section 1: Customer Information */}
            <div
              id="section-customer"
              data-section="customer"
              className="cx-form-card"
            >
              <div className="cx-form-card-header"><T>1. Customer Information</T></div>
              <GeneralFields 
                formData={formData} 
                handleChange={handleChange} 
                handleBlur={handleBlur} 
                errors={errors} 
                touched={touched} 
              />
            </div>

            {/* Section 2: Ticket Details */}
            <div
              id="section-ticket"
              data-section="ticket"
              className="cx-form-card"
            >
              <div className="cx-form-card-header"><T>2. Ticket Details</T></div>
              <TicketDetailsFields
                formData={formData}
                handleChange={handleChange}
                handleBlur={handleBlur}
                onCaseTypeChange={handleCaseTypeChange}
                onCaseReasonChange={handleCaseReasonChange}
                errors={errors}
                touched={touched}
              />
              <SubjectField 
                formData={formData} 
                handleChange={handleChange} 
                handleBlur={handleBlur} 
                errors={errors} 
                touched={touched} 
              />
              <DescriptionField
                formData={formData}
                handleChange={handleChange}
                handleBlur={handleBlur}
                errors={errors}
                touched={touched}
              />
            </div>

            {/* Section 3: Kenya Specific (conditional) */}
            {isKenya && (
              <div
                id="section-kenya"
                data-section="kenya"
                className="cx-form-card"
              >
                <div className="cx-form-card-header">
                  <T>3. Kenya Specific Information</T>
                </div>
                <CountrySpecificFields
                  formData={formData}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  errors={errors}
                  touched={touched}
                />
              </div>
            )}

            {/* Section: Attachments */}
            <div
              id="section-attachments"
              data-section="attachments"
              className="cx-form-card"
            >
              <div className="cx-form-card-header">
                {isKenya ? "4" : "3"}. <T>Attachments</T>
              </div>
              <CustomerFileAttachment
                attachments={formData.attachments}
                onChange={handleAddFiles}
                onRemove={removeAttachment}
              />
            </div>

            {/* Pinned Submit */}
            <div className="cx-pinned-submit">
              <div className="cx-security-note">
                <div className="cx-security-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="3"
                      y="11"
                      width="18"
                      height="11"
                      rx="2"
                      ry="2"
                    ></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </div>
                <T>Your submission goes securely to Autochek's Customer Experience Team.</T>
              </div>
              <div 
                style={{ display: 'inline-block' }}
                onClick={() => {
                  if (!formValid) {
                    setAllTouched();
                  }
                }}
              >
                <button
                  type="submit"
                  className="btn-submit"
                  onClick={formValid ? handleSubmit : undefined}
                  disabled={status.type === "loading" || !formValid}
                  style={{ flex: "0 0 auto", padding: "14px 32px", pointerEvents: (!formValid || status.type === "loading") ? 'none' : 'auto' }}
                >
                  {status.type === "loading" ? (
                    <T>Submitting…</T>
                  ) : (
                    <>
                      <span><T>Submit Ticket</T></span> <SendIcon />
                    </>
                  )}
                </button>
              </div>
            </div>

            <StatusToast status={status} />
          </form>
        </div>
      </main>
    </div>
  );
}
